import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, PackageOpen, CircleDot, Circle, CheckCircle2, ChevronDown, ChevronRight, Folder, Plus, MoreVertical, Pencil, Copy as CopyIcon, Trash2 } from "lucide-react";
import DeliverableEditModal from "@/components/DeliverableEditModal";
import {
  DndContext, PointerSensor, useSensor, useSensors,
  closestCorners, DragOverlay, useDroppable,
} from "@dnd-kit/core";
import { useDraggable } from "@dnd-kit/core";

/**
 * iter37 — Deliverables Pipeline board.
 *
 *  Auto-seeded from booking.deliverables. Renders as a horizontal set of
 *  drop-zones: leftmost is the "Unassigned Pool", followed by one lane per
 *  active staff member. Coordinators drag task chips between lanes to
 *  update `assigned_staff_id`; a click on the chip body cycles the status
 *  (pending → in_progress → done → pending).
 *
 *  Lives above the month-scoped Work Allocation kanban on /team.
 */

const STATUS_CYCLE = ["pending", "in_progress", "done"];
const STATUS_STYLES = {
  pending:     { chip: "bg-white border-stone-300 text-stone-700",       icon: Circle,        badge: "bg-stone-100 text-stone-600" },
  in_progress: { chip: "bg-amber-50 border-amber-300 text-amber-900",    icon: CircleDot,     badge: "bg-amber-100 text-amber-800" },
  done:        { chip: "bg-emerald-50 border-emerald-300 text-emerald-900", icon: CheckCircle2, badge: "bg-emerald-100 text-emerald-800" },
};
const STATUS_LABELS = { pending: "Pending", in_progress: "In-Progress", done: "Done" };

const UNASSIGNED = "__unassigned__";

export default function DeliverablesPipeline({ canWrite }) {
  const [staff, setStaff] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTask, setActiveTask] = useState(null);
  // iter105 — Hide DONE tasks from Editor lanes by default. Persisted in
  // localStorage so the coordinator's preference survives reloads. The
  // Unassigned Pool ignores this toggle (unassigned = never done).
  const [showCompleted, setShowCompleted] = useState(() => {
    try { return localStorage.getItem("kahani.pipeline.showCompleted") === "1"; } catch { return false; }
  });
  // iter160 — Add/Edit modal state + bookings list for the picker.
  const [modalMode, setModalMode] = useState(null); // null | {type:"create"} | {type:"edit", task}
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    try { localStorage.setItem("kahani.pipeline.showCompleted", showCompleted ? "1" : "0"); } catch { /* ignore */ }
  }, [showCompleted]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: t }] = await Promise.all([
        // iter128 — Deliverables Pipeline is editing-only. Freelancers are
        // excluded so their lanes never appear on the kanban. Existing
        // assignments in the DB remain untouched (never deleted).
        api.get("/team/staff?exclude_freelance=true"),
        api.get("/post-production/tasks"),
      ]);
      setStaff(s);
      setTasks(t);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // iter160 — Lazy-load bookings when the modal opens (not on every render).
  useEffect(() => {
    if (!modalMode) return;
    if (bookings.length) return;
    api.get("/bookings").then(({ data }) => setBookings(data || [])).catch(() => {});
  }, [modalMode, bookings.length]);

  const byColumn = useMemo(() => {
    const m = new Map();
    m.set(UNASSIGNED, []);
    staff.forEach((s) => m.set(s.id, []));
    for (const t of tasks) {
      const k = t.assigned_staff_id || UNASSIGNED;
      if (!m.has(k)) m.set(k, []);
      // iter105 — Hide DONE tasks from Editor lanes unless the "Show
      // Completed History" toggle is on. Never filter from the Unassigned
      // Pool — those cards are by definition not done, and we don't want
      // to accidentally hide backlog items.
      if (k !== UNASSIGNED && t.status === "done" && !showCompleted) continue;
      m.get(k).push(t);
    }
    return m;
  }, [staff, tasks, showCompleted]);

  // iter105 — Count of DONE tasks currently hidden from editor lanes so we
  // can surface the number on the "Show Completed History" checkbox.
  const hiddenDoneCount = useMemo(
    () => tasks.filter((t) => t.assigned_staff_id && t.status === "done").length,
    [tasks],
  );

  const handleDragStart = ({ active }) => {
    setActiveTask(tasks.find((t) => t.id === active.id) || null);
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveTask(null);
    if (!over) return;
    const overId = over.id;
    const task = tasks.find((t) => t.id === active.id);
    if (!task) return;
    const nextStaffId = overId === UNASSIGNED ? null : overId;
    if ((task.assigned_staff_id || null) === nextStaffId) return;   // no-op
    // Optimistic update.
    const staffObj = staff.find((s) => s.id === nextStaffId);
    setTasks((prev) => prev.map((t) => t.id === task.id ? {
      ...t,
      assigned_staff_id: nextStaffId,
      assigned_staff_name: staffObj ? staffObj.name : "",
    } : t));
    try {
      await api.patch(`/post-production/tasks/${task.id}`, {
        assigned_staff_id: nextStaffId === null ? "" : nextStaffId,
      });
      if (nextStaffId) toast.success(`Assigned "${task.label}" → ${staffObj?.name}`);
      else toast.info(`"${task.label}" moved back to Unassigned pool`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reassign — reloading");
      load();
    }
  };

  const cycleStatus = async (task) => {
    if (!canWrite) return;
    const idx = STATUS_CYCLE.indexOf(task.status);
    const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
    setTasks((prev) => prev.map((t) => t.id === task.id ? { ...t, status: next } : t));
    try {
      await api.patch(`/post-production/tasks/${task.id}`, { status: next });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update status");
      load();
    }
  };

  const columns = [
    { id: UNASSIGNED, name: "Unassigned Pool", hint: "Auto-seeded from bookings", isPool: true },
    ...staff.map((s) => ({ id: s.id, name: s.name })),
  ];

  return (
    <div className="space-y-3" data-testid="deliverables-pipeline">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <PackageOpen className="w-5 h-5 text-rose-700" strokeWidth={1.8} />
            Deliverables Pipeline
          </h2>
          <p className="text-[11px] text-stone-500 leading-relaxed">
            Auto-seeded from every booking&apos;s <b>Deliverables</b> list. Drag a task from the <span className="font-semibold text-stone-700">Unassigned Pool</span> onto a coordinator&apos;s lane to assign. Click a chip to cycle status: <span className="text-stone-600">Pending</span> → <span className="text-amber-700">In-Progress</span> → <span className="text-emerald-700">Done</span>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* iter105 — Show Completed History toggle. Default OFF so Editor
               lanes stay focused on active work; the coordinator flips it
               on to audit past deliveries. */}
          <label className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-stone-600 select-none cursor-pointer" data-testid="pipeline-show-completed-label">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-stone-300 text-emerald-700 focus:ring-emerald-500"
              data-testid="pipeline-show-completed-toggle"
            />
            Show Completed History
            {hiddenDoneCount > 0 && !showCompleted && (
              <span className="text-[10px] font-mono text-stone-500 bg-stone-100 border border-stone-200 rounded-full px-1.5 py-0.5" data-testid="pipeline-hidden-count">
                {hiddenDoneCount} hidden
              </span>
            )}
          </label>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-stone-400" />}
        </div>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveTask(null)}
      >
        <div className="card overflow-x-auto">
          <div className="min-w-full flex divide-x divide-stone-200" data-testid="deliverables-board">
            {columns.map((col) => (
              <PipelineColumn
                key={col.id}
                col={col}
                tasks={byColumn.get(col.id) || []}
                canWrite={canWrite}
                onCycle={cycleStatus}
                onAdd={canWrite ? () => setModalMode({ type: "create" }) : null}
                onEdit={canWrite ? (task) => setModalMode({ type: "edit", task }) : null}
              />
            ))}
          </div>
        </div>
        <DragOverlay dropAnimation={{ duration: 150 }}>
          {activeTask ? <TaskChip task={activeTask} isOverlay /> : null}
        </DragOverlay>
      </DndContext>
      {modalMode && (
        <DeliverableEditModal
          task={modalMode.type === "edit" ? modalMode.task : null}
          bookings={bookings}
          staff={staff}
          onClose={() => setModalMode(null)}
          onSaved={load}
          onDeleted={load}
          onDuplicated={load}
        />
      )}
    </div>
  );
}

function PipelineColumn({ col, tasks, canWrite, onCycle, onAdd, onEdit }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });
  const isEmpty = tasks.length === 0;

  // iter105 — For the Unassigned Pool, group tasks by couple_name into
  // collapsible accordion "folders" so a large backlog stays scannable.
  // Editor lanes render tasks as a flat list (no folders — each lane is
  // already one coordinator's tightly-scoped queue).
  // iter106 — Sort folders chronologically by earliest `event_date` (ISO
  // yyyy-mm-dd, so lexicographic comparison is also chronological). Couples
  // with no event_date fall to the bottom, tie-broken alphabetically for
  // deterministic order.
  const grouped = useMemo(() => {
    if (!col.isPool) return null;
    const buckets = new Map();
    for (const t of tasks) {
      const key = (t.couple_name || t.client_name || "— Unnamed —").trim() || "— Unnamed —";
      if (!buckets.has(key)) buckets.set(key, { tasks: [], earliestDate: "" });
      const bucket = buckets.get(key);
      bucket.tasks.push(t);
      const d = String(t.event_date || "").trim();
      if (d && (!bucket.earliestDate || d < bucket.earliestDate)) bucket.earliestDate = d;
    }
    return Array.from(buckets.entries()).sort(([aName, a], [bName, b]) => {
      const ad = a.earliestDate, bd = b.earliestDate;
      if (!ad && !bd) return aName.localeCompare(bName);
      if (!ad) return 1;   // undated → bottom
      if (!bd) return -1;
      if (ad !== bd) return ad.localeCompare(bd);
      return aName.localeCompare(bName);
    });
  }, [tasks, col.isPool]);

  return (
    <div
      className={`w-60 shrink-0 transition-colors ${col.isPool ? "bg-rose-50/40" : "bg-stone-50/40"} ${isOver ? "ring-2 ring-rose-300 ring-inset" : ""}`}
      data-testid={`deliverables-col-${col.id}`}
    >
      <div className={`sticky top-0 z-10 border-b px-3 py-2 ${col.isPool ? "bg-rose-100 border-rose-200" : "bg-stone-100 border-stone-200"}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className={`text-[11px] uppercase tracking-wider font-semibold ${col.isPool ? "text-rose-800" : "text-stone-600"}`}>
              {col.name}
            </div>
            {col.hint && <div className="text-[9px] text-rose-700/70 italic">{col.hint}</div>}
            <div className="text-[10px] text-stone-500 font-mono">{tasks.length} task{tasks.length === 1 ? "" : "s"}</div>
          </div>
          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-md bg-white/80 hover:bg-white border border-stone-200 text-rose-700"
              title="Add deliverable"
              data-testid={`deliverables-add-${col.id}`}
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={2.2} />
            </button>
          )}
        </div>
      </div>
      <div ref={setNodeRef} className="p-2 space-y-2 min-h-[100px]">
        {isEmpty && (
          <div className="text-[10px] text-stone-400 italic text-center py-4 border border-dashed border-stone-200 rounded" data-testid={`deliverables-empty-${col.id}`}>
            {col.isPool ? "No unassigned tasks 🎉" : "Drop tasks here"}
          </div>
        )}
        {col.isPool && grouped ? (
          grouped.map(([couple, bucket]) => (
            <CoupleFolder
              key={couple}
              couple={couple}
              tasks={bucket.tasks}
              earliestDate={bucket.earliestDate}
              canWrite={canWrite}
              onCycle={onCycle}
              onEdit={onEdit}
            />
          ))
        ) : (
          tasks.map((t) => (
            <DraggableTask key={t.id} task={t} canWrite={canWrite} onCycle={onCycle} onEdit={onEdit} />
          ))
        )}
      </div>
    </div>
  );
}

// iter105 — Collapsible accordion for one couple's tasks inside the
// Unassigned Pool. Collapsed by default (user asked for this) — click the
// header to expand. Uses `useState` locally so each folder tracks its own
// open state and remembers it across re-renders while the coordinator is
// dragging tasks around.
// iter106 — Displays the couple's earliest shoot date under the name so
// coordinators can visually prioritise the oldest / most-urgent shoots
// first without opening every folder.
function CoupleFolder({ couple, tasks, earliestDate = "", canWrite, onCycle, onEdit }) {
  const [open, setOpen] = useState(false);
  const testKey = couple.replace(/\s+/g, "-").toLowerCase();
  // "yyyy-mm-dd" → "01 Aug 2026" (best-effort; falls back to raw if unparseable).
  const formattedDate = (() => {
    if (!earliestDate) return "";
    const d = new Date(earliestDate);
    if (Number.isNaN(d.getTime())) return earliestDate;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  })();
  return (
    <div className="rounded-md border border-stone-200 bg-white/70 overflow-hidden" data-testid={`deliverables-couple-folder-${testKey}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 px-2 py-1.5 hover:bg-rose-50/60 transition-colors text-left"
        data-testid={`deliverables-couple-folder-toggle-${testKey}`}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-rose-700 shrink-0" strokeWidth={2.2} /> : <ChevronRight className="w-3.5 h-3.5 text-stone-500 shrink-0" strokeWidth={2.2} />}
        <Folder className="w-3.5 h-3.5 text-amber-600 shrink-0" strokeWidth={2} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-stone-800 truncate" title={couple}>{couple}</div>
          {formattedDate && (
            <div
              className="text-[10px] text-stone-500 font-mono truncate"
              data-testid={`deliverables-couple-folder-date-${testKey}`}
              title={`Earliest shoot date · ${formattedDate}`}
            >
              {formattedDate}
            </div>
          )}
        </div>
        <span className="text-[10px] font-mono text-stone-500 bg-stone-100 border border-stone-200 rounded-full px-1.5 py-0.5" data-testid={`deliverables-couple-folder-count-${testKey}`}>
          {tasks.length}
        </span>
      </button>
      {open && (
        <div className="p-1.5 pt-0 space-y-1.5 border-t border-stone-100 bg-white/40" data-testid={`deliverables-couple-folder-body-${testKey}`}>
          {tasks.map((t) => (
            <DraggableTask key={t.id} task={t} canWrite={canWrite} onCycle={onCycle} onEdit={onEdit} />
          ))}
        </div>
      )}
    </div>
  );
}

function DraggableTask({ task, canWrite, onCycle, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    opacity: isDragging ? 0.35 : 1,
  } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="touch-none"
      data-testid={`deliverables-task-${task.id}`}
    >
      <TaskChip
        task={task}
        canWrite={canWrite}
        onCycle={onCycle}
        onEdit={onEdit}
        dragHandleProps={{ ...listeners, ...attributes }}
      />
    </div>
  );
}

function TaskChip({ task, canWrite, onCycle, onEdit, dragHandleProps, isOverlay }) {
  const style = STATUS_STYLES[task.status] || STATUS_STYLES.pending;
  const Icon = style.icon;
  return (
    <div
      className={`border rounded-md px-2 py-1.5 shadow-sm ${style.chip} ${isOverlay ? "shadow-lg rotate-1 scale-105" : ""}`}
    >
      <div className="flex items-start gap-1.5">
        <button
          type="button"
          {...(dragHandleProps || {})}
          className="text-stone-400 hover:text-stone-700 cursor-grab active:cursor-grabbing shrink-0 mt-0.5"
          title="Drag to reassign"
          aria-label="Drag to reassign"
        >
          <span className="block w-2 h-3 border-l-2 border-r-2 border-current" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-xs truncate" title={task.source}>{task.label}</div>
          <div className="text-[10px] text-stone-500 truncate">{task.couple_name || task.client_name || "—"}</div>
          <div className="flex items-center justify-between gap-1 mt-1">
            <button
              type="button"
              disabled={!canWrite || isOverlay}
              onClick={() => canWrite && onCycle && onCycle(task)}
              className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${style.badge} ${canWrite ? "hover:brightness-95 cursor-pointer" : "cursor-default"}`}
              title="Click to cycle status"
              data-testid={`deliverables-status-${task.id}`}
            >
              <Icon className="w-3 h-3" strokeWidth={2} />
              {STATUS_LABELS[task.status] || task.status}
            </button>
            {canWrite && onEdit && !isOverlay && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onEdit(task); }}
                className="text-stone-400 hover:text-rose-700 p-0.5 rounded"
                title="Edit deliverable"
                aria-label="Edit deliverable"
                data-testid={`deliverables-edit-${task.id}`}
              >
                <Pencil className="w-3 h-3" strokeWidth={2} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
